import { Select } from 'antd';
import * as Component from 'components';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { videoSourceService } from 'upload/video-source/service/videoSourceService';
import {
  VideoSourceSelectItemInterface,
  VideoSourceVideoListInterface,
} from 'upload/video-source/type';
import { getVideoSourceContentListColumns } from './VideoSourceContentColumns';

const VideoSourceContent: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const videoSourceType = location.state?.videoSourceType;
  const [videoSourceList, setVideoSourceList] = useState<VideoSourceSelectItemInterface[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selectedVideoSource, setSelectedVideoSource] = useState<number>();

  const fetchVideoSourceContent = useCallback(
    async (
      params: URLSearchParams
    ): Promise<{ data: VideoSourceVideoListInterface[]; count: number }> => {
      if (selectedVideoSource === undefined || selectedVideoSource === null) {
        return { data: [], count: 0 };
      }

      const queryParams = new URLSearchParams({
        page: params.get('page') ?? '',
        page_size: params.get('page_size') ?? '',
        search: params.get('search') ?? '',
      });

      const videoSourceContentList =
        (await videoSourceService.fetchContentList(selectedVideoSource, queryParams)) || [];

      return {
        data: videoSourceContentList,
        count: videoSourceContentList.length,
      };
    },
    [searchParams, selectedVideoSource]
  );

  const handleChange = (value: string): void => {
    setSelectedVideoSource(Number(value));
    setRefreshCounter(prev => prev + 1);
  };

  const columns = getVideoSourceContentListColumns();

  useEffect(() => {
    if (videoSourceType === 'cp') {
      const fetchVideoSourceList = async (): Promise<void> => {
        const videoSourceList = await videoSourceService.getVideoSourceSelectItemList();
        const filteredVideoSourceList = videoSourceList.filter(item => item.name !== 'ODK');
        setVideoSourceList(filteredVideoSourceList);
        if (filteredVideoSourceList.length > 0) {
          setSelectedVideoSource(filteredVideoSourceList[0].id);
        }
      };
      fetchVideoSourceList().then(() => {
        setIsLoading(false);
      });
    } else if (videoSourceType === 'odk') {
      setSelectedVideoSource(1);
      setIsLoading(false);
    } else {
      setSelectedVideoSource(0);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <div>
      <Component.BasePageComponent>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          {videoSourceType === 'cp' && (
            <>
              <div style={{ marginRight: '10px' }}>Video Source</div>
              <Select
                style={{ width: '200px' }}
                onChange={handleChange}
                value={selectedVideoSource?.toString()} // Match as string
              >
                {videoSourceList.map(videoSource => (
                  <Select.Option key={videoSource.id} value={videoSource.id.toString()}>
                    {videoSource.name}
                  </Select.Option>
                ))}
              </Select>
            </>
          )}
        </div>
        {isLoading ? (
          <Component.LoadingSpinnerComponent />
        ) : (
          <Component.TableComponent<VideoSourceVideoListInterface>
            key={refreshCounter}
            name={
              videoSourceType === 'user'
                ? 'Uploaded Contents'
                : videoSourceType === 'odk'
                  ? 'Imported Contents From Sync'
                  : 'VideoSourceContent'
            }
            columns={columns}
            options={{ isDisableIdColumn: true }}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            dataFetch={fetchVideoSourceContent}
          />
        )}
      </Component.BasePageComponent>
    </div>
  );
};

export default observer(VideoSourceContent);
